import { Order } from '@/types';
import { auth } from '@/lib/firebase';
import { Auth } from 'firebase/auth';

/**
 * Get authorization header with ID token
 */
async function getAuthHeader(): Promise<string> {
  if (!auth) {
    throw new Error('Auth not initialized');
  }
  const user = (auth as Auth).currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  const idToken = await user.getIdToken();
  return `Bearer ${idToken}`;
}

/**
 * Get all orders (for admin) - via API
 */
export async function getAllOrdersAdmin(options?: {
  status?: string;
  limit?: number;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
}): Promise<Order[]> {
  try {
    const authHeader = await getAuthHeader();
    const params = new URLSearchParams();
    
    if (options?.status) params.append('status', options.status);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.orderBy) params.append('orderBy', options.orderBy);
    if (options?.orderDirection) params.append('orderDirection', options.orderDirection);

    const queryString = params.toString();
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    const url = `${apiBaseUrl}/api/admin/orders${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
      headers: {
        'Authorization': authHeader,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch orders');
    }

    const data = await response.json();
    return data.orders || [];
  } catch (error) {
    console.error('Error fetching orders:', error);
    throw error;
  }
}

/**
 * Get a single order by ID - via API
 */
export async function getOrderById(orderId: string): Promise<Order | null> {
  try {
    const authHeader = await getAuthHeader();
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    const response = await fetch(`${apiBaseUrl}/api/admin/orders/${orderId}`, {
      headers: {
        'Authorization': authHeader,
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch order');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching order:', error);
    throw error;
  }
}

/**
 * Update order status - via API
 */
export async function updateOrderStatus(
  orderId: string,
  status: Order['status']
): Promise<void> {
  try {
    const authHeader = await getAuthHeader();
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    const response = await fetch(`${apiBaseUrl}/api/admin/orders/${orderId}`, {
      method: 'PUT',
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update order status');
    }
  } catch (error) {
    console.error('Error updating order status:', error);
    throw error;
  }
}

/**
 * Update order payment status - via API
 */
export async function updateOrderPaymentStatus(
  orderId: string,
  paymentStatus: Order['payment']['status']
): Promise<void> {
  try {
    const authHeader = await getAuthHeader();
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    const response = await fetch(`${apiBaseUrl}/api/admin/orders/${orderId}`, {
      method: 'PUT',
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        payment: {
          status: paymentStatus,
        },
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update payment status');
    }
  } catch (error) {
    console.error('Error updating payment status:', error);
    throw error;
  }
}

/**
 * Add comment to order - via API
 */
export async function addOrderComment(
  orderId: string,
  commentText: string,
  createdBy: string
): Promise<void> {
  try {
    const authHeader = await getAuthHeader();
    const order = await getOrderById(orderId);
    
    if (!order) {
      throw new Error('Order not found');
    }

    const newComment = {
      id: Date.now().toString() + Math.random().toString(36).substring(2, 9),
      text: commentText,
      createdAt: new Date().toISOString(),
      createdBy,
    };

    const existingComments = order.comments || [];
    const updatedComments = [...existingComments, newComment];

    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    const response = await fetch(`${apiBaseUrl}/api/admin/orders/${orderId}`, {
      method: 'PUT',
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        comments: updatedComments,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to add comment');
    }
  } catch (error) {
    console.error('Error adding comment:', error);
    throw error;
  }
}

/**
 * Delete comment from order - via API
 */
export async function deleteOrderComment(
  orderId: string,
  commentId: string
): Promise<void> {
  try {
    const authHeader = await getAuthHeader();
    const order = await getOrderById(orderId);
    
    if (!order) {
      throw new Error('Order not found');
    }

    const existingComments = order.comments || [];
    const updatedComments = existingComments.filter((c) => c.id !== commentId);

    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    const response = await fetch(`${apiBaseUrl}/api/admin/orders/${orderId}`, {
      method: 'PUT',
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        comments: updatedComments,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete comment');
    }
  } catch (error) {
    console.error('Error deleting comment:', error);
    throw error;
  }
}

/**
 * Create invoice for order - via API
 */
export async function createOrderInvoice(orderId: string): Promise<{ invoiceId: string; invoicePdfUrl: string | null }> {
  try {
    const authHeader = await getAuthHeader();
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    const response = await fetch(`${apiBaseUrl}/api/admin/orders/${orderId}/invoice`, {
      method: 'POST',
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create invoice');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating invoice:', error);
    throw error;
  }
}

/**
 * Get invoice PDF URL for order - via API
 */
export async function getOrderInvoicePdf(orderId: string): Promise<string> {
  try {
    const authHeader = await getAuthHeader();
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    const response = await fetch(`${apiBaseUrl}/api/admin/orders/${orderId}/invoice/pdf`, {
      headers: {
        'Authorization': authHeader,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get invoice PDF');
    }

    const data = await response.json();
    return data.pdfUrl;
  } catch (error) {
    console.error('Error getting invoice PDF:', error);
    throw error;
  }
}

